import React, {FC} from 'react';

import {createMandatoryContext} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {BaseEditorContext, createBaseEditorContext, CreateBaseEditorContextProps} from '../baseEditorContext';
import {
  CreateExpressionContextProps,
  createExpressionsContext,
  ExpressionsContext,
} from '../expressions/expressionsContext';
import {PreviewerResult} from '../expressions/infer/expressionResultModel';

/*
 * Types.
 */

export interface PreviewerEditorContext extends BaseEditorContext, ExpressionsContext {
  application: LocalApplicationModel;
}

// Omitting `initialInfer` from `CreateExpressionContextProps` as we'll use `PreviewerResult` in the rule editor.
interface CreatePreviewerEditorContextProps
  extends CreateBaseEditorContextProps,
    Omit<CreateExpressionContextProps, 'initialInfer'> {
  application: LocalApplicationModel;
}

/*
 * Context.
 */

export const [PreviewerEditorContext, usePreviewerEditorContext] =
  createMandatoryContext<PreviewerEditorContext>();

export const PreviewerEditorContextProvider: FC<{context: PreviewerEditorContext}> = ({
  context,
  children,
}) => (
  <BaseEditorContext.Provider value={context}>
    <ExpressionsContext.Provider value={context}>
      <PreviewerEditorContext.Provider value={context}>{children}</PreviewerEditorContext.Provider>
    </ExpressionsContext.Provider>
  </BaseEditorContext.Provider>
);

export const createPreviewerEditorContext = ({
  namespace,
  isEditing,
  hasExpressions,
  hasHighlightErrorsFlag,
  expressions,
  customFields,
  application,
  setPanelOpen,
  expressionCreationState,
  setExpressionCreationState,
  isReadOnly = false,
}: CreatePreviewerEditorContextProps): PreviewerEditorContext => {
  const baseEditorContext = createBaseEditorContext({
    namespace,
    isEditing,
    isReadOnly,
    hasExpressions,
    hasHighlightErrorsFlag,
  });

  const expressionsContext = createExpressionsContext({
    expressions,
    customFields,
    initialInfer: PreviewerResult,
    setPanelOpen,
    expressionCreationState,
    setExpressionCreationState,
  });

  return {
    ...baseEditorContext,
    ...expressionsContext,
    application,
  };
};
